import { Button, Group, Select, Stack, Switch, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { CityDistrictRO } from '@routellers/types/dist/city-district/city-district.ro';
import { GuideLocaleStatusEnum } from '@routellers/types/dist/city-guide/enum/guide-locale-status.enum';
import {
    GUIDE_STATUS_ORDER,
    GuideStatusEnum,
} from '@routellers/types/dist/city-guide/enum/guide-status.enum';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { EditorFooter, EntityEditorShell, LanguageChips, Section } from '../components/editor';
import { enumOptions } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';

import { CitySearchSelect } from './city-search-select';

type Props = {
    opened: boolean;
    onClose: () => void;
    districtId: number | null;
    defaultCityId?: number | null;
    onSaved: () => void;
    onCreated?: (id: number) => void;
    onDeleted?: () => void;
};

type CardForm = {
    title: string;
    slug: string;
    cityId: number | null;
    lat: string;
    lng: string;
    status: GuideStatusEnum;
    coverUrl: string;
    coverAuthor: string;
    coverLicense: string;
    coverSourceUrl: string;
};

type LocaleDraft = {
    name: string;
    localName: string;
    characterLine: string;
    portrait: string;
    why: string;
    stay: string;
    knownFor: string;
    streets: string;
    tags: string;
    guideTip: string;
    guideName: string;
    guideRole: string;
    status: GuideLocaleStatusEnum;
};

const statusColor: Record<GuideStatusEnum, string> = {
    [GuideStatusEnum.DRAFT]: 'gray',
    [GuideStatusEnum.PUBLISHED]: 'green',
};

const emptyCard = (cityId?: number | null): CardForm => ({
    title: '',
    slug: '',
    cityId: cityId ?? null,
    lat: '',
    lng: '',
    status: GuideStatusEnum.DRAFT,
    coverUrl: '',
    coverAuthor: '',
    coverLicense: '',
    coverSourceUrl: '',
});

const emptyLocale = (): LocaleDraft => ({
    name: '',
    localName: '',
    characterLine: '',
    portrait: '',
    why: '',
    stay: '',
    knownFor: '',
    streets: '',
    tags: '',
    guideTip: '',
    guideName: '',
    guideRole: '',
    status: GuideLocaleStatusEnum.EMPTY,
});

const parseCoord = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) return null;

    const parsed = Number(trimmed);

    return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const cardFrom = (row: CityDistrictRO): CardForm => ({
    title: row.title || '',
    slug: row.slug || '',
    cityId: row.cityId,
    lat: row.lat == null ? '' : String(row.lat),
    lng: row.lng == null ? '' : String(row.lng),
    status: row.status,
    coverUrl: row.coverUrl || '',
    coverAuthor: row.coverAuthor || '',
    coverLicense: row.coverLicense || '',
    coverSourceUrl: row.coverSourceUrl || '',
});

export const DistrictEditor = ({
    opened,
    onClose,
    districtId,
    defaultCityId,
    onSaved,
    onCreated,
    onDeleted,
}: Props) => {
    const { t } = useTranslation();
    const isCreate = districtId == null;
    const [row, setRow] = useState<CityDistrictRO | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('card');
    const [lang, setLang] = useState<Languages>(CONTENT_BASE_LANGUAGE);
    const [drafts, setDrafts] = useState<Record<string, LocaleDraft>>({});
    const form = useForm<CardForm>({ initialValues: emptyCard(defaultCityId) });

    const languages = contentLanguagesForCountry(row?.country);

    const applyRow = (next: CityDistrictRO) => {
        setRow(next);
        form.setValues(cardFrom(next));
        form.resetDirty();
        const nextDrafts: Record<string, LocaleDraft> = {};

        for (const language of contentLanguagesForCountry(next.country)) {
            const locale = (next.locales || []).find((item) => item.language === language);

            nextDrafts[language] = locale
                ? {
                      name: locale.name || '',
                      localName: locale.localName || '',
                      characterLine: locale.characterLine || '',
                      portrait: locale.portrait || '',
                      why: locale.why || '',
                      stay: locale.stay || '',
                      knownFor: locale.knownFor || '',
                      streets: locale.streets || '',
                      tags: (locale.tags || []).join(', '),
                      guideTip: locale.guideTip || '',
                      guideName: locale.guideName || '',
                      guideRole: locale.guideRole || '',
                      status: locale.status,
                  }
                : emptyLocale();
        }

        setDrafts(nextDrafts);
    };

    useEffect(() => {
        if (!opened) return;

        setTab('card');
        setLang(CONTENT_BASE_LANGUAGE);

        if (districtId == null) {
            setRow(null);
            form.setValues(emptyCard(defaultCityId));
            form.resetDirty();
            setDrafts({});

            return;
        }

        let cancelled = false;

        setLoading(true);
        api.cityDistrictsApi
            .getOne(districtId)
            .then(({ data }) => {
                if (!cancelled && data) applyRow(data);
            })
            .catch((error) => {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, districtId]);

    const statusOptions = enumOptions(GUIDE_STATUS_ORDER, t, 'enums.guideStatus');
    const current = drafts[lang] || emptyLocale();
    const chips = useMemo(
        () =>
            languages.map((language) => {
                const item = drafts[language] || emptyLocale();
                let mark: 'filled' | 'reviewed' | 'empty' = 'empty';

                if (item.status === GuideLocaleStatusEnum.READY) mark = 'reviewed';
                else if (
                    item.name.trim() ||
                    item.localName.trim() ||
                    item.characterLine.trim() ||
                    item.portrait.trim() ||
                    item.why.trim() ||
                    item.stay.trim() ||
                    item.knownFor.trim() ||
                    item.streets.trim() ||
                    item.tags.trim() ||
                    item.guideTip.trim()
                ) {
                    mark = 'filled';
                }

                return { value: language, label: language.toUpperCase(), mark };
            }),
        [languages, drafts],
    );

    const patchDraft = (patch: Partial<LocaleDraft>) => {
        setDrafts((prev) => ({
            ...prev,
            [lang]: { ...(prev[lang] || emptyLocale()), ...patch },
        }));
    };

    const saveCard = async () => {
        const title = form.values.title.trim();

        if (!title || !form.values.cityId) {
            notifications.show({ color: 'red', message: t('pages.districts.editor.required') });

            return;
        }

        const lat = parseCoord(form.values.lat);
        const lng = parseCoord(form.values.lng);

        if (Number.isNaN(lat) || Number.isNaN(lng) || (lat == null) !== (lng == null)) {
            notifications.show({ color: 'red', message: t('pages.districts.editor.coords') });

            return;
        }

        const readySaved = (row?.locales || []).some(
            (locale) => locale.status === GuideLocaleStatusEnum.READY,
        );

        if (form.values.status === GuideStatusEnum.PUBLISHED && !readySaved) {
            notifications.show({
                color: 'red',
                message: t('pages.districts.editor.publishNeedsText'),
            });

            return;
        }

        const payload = {
            title,
            slug: form.values.slug.trim() || null,
            cityId: form.values.cityId,
            lat,
            lng,
            status: form.values.status,
            coverUrl: form.values.coverUrl.trim() || null,
            coverAuthor: form.values.coverAuthor.trim() || null,
            coverLicense: form.values.coverLicense.trim() || null,
            coverSourceUrl: form.values.coverSourceUrl.trim() || null,
        };

        setSaving(true);

        try {
            if (isCreate) {
                const { data } = await api.cityDistrictsApi.create({ data: payload });

                notifications.show({ color: 'green', message: t('pages.districts.editor.created') });
                onSaved();
                if (data?.id) onCreated?.(data.id);
            } else {
                const { data } = await api.cityDistrictsApi.update({ id: districtId!, data: payload });

                if (data) applyRow(data);
                notifications.show({ color: 'green', message: t('pages.districts.editor.saved') });
                onSaved();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const saveLocale = async () => {
        if (!districtId) return;

        if (
            current.status === GuideLocaleStatusEnum.READY &&
            (!current.name.trim() ||
                (!current.portrait.trim() && !current.why.trim() && !current.stay.trim()))
        ) {
            notifications.show({
                color: 'red',
                message: t('pages.districts.editor.readyNeedsCopy'),
            });

            return;
        }

        setSaving(true);

        try {
            const { data } = await api.cityDistrictsApi.upsertLocale({
                id: districtId,
                data: {
                    language: lang,
                    name: current.name.trim(),
                    localName: current.localName.trim(),
                    characterLine: current.characterLine.trim(),
                    portrait: current.portrait.trim() || null,
                    why: current.why.trim() || null,
                    stay: current.stay.trim() || null,
                    knownFor: current.knownFor.trim() || null,
                    streets: current.streets.trim(),
                    tags: current.tags
                        .split(',')
                        .map((tag) => tag.trim())
                        .filter(Boolean),
                    guideTip: current.guideTip.trim() || null,
                    guideName: current.guideName.trim(),
                    guideRole: current.guideRole.trim(),
                    status: current.status,
                },
            });

            if (data) applyRow(data);
            notifications.show({ color: 'green', message: t('pages.districts.editor.saved') });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!districtId || !row) return;
        if (!window.confirm(t('pages.districts.editor.confirmDelete', { title: row.title }))) return;

        setSaving(true);

        try {
            await api.cityDistrictsApi.remove(districtId);
            notifications.show({ color: 'green', message: t('pages.districts.editor.deleted') });
            onDeleted?.();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <EntityEditorShell
            opened={opened}
            onClose={onClose}
            loading={loading}
            title={
                isCreate
                    ? t('pages.districts.editor.newTitle')
                    : row?.title || t('pages.districts.editor.fallbackTitle', { id: districtId })
            }
            subtitle={row?.cityTitle || undefined}
            status={
                row
                    ? {
                          label: t(`enums.guideStatus.${row.status}`),
                          color: statusColor[row.status],
                      }
                    : undefined
            }
            tabs={
                isCreate
                    ? [{ value: 'card', label: t('pages.districts.editor.tabs.card') }]
                    : [
                          { value: 'card', label: t('pages.districts.editor.tabs.card') },
                          { value: 'text', label: t('pages.districts.editor.tabs.text') },
                      ]
            }
            activeTab={tab}
            onTabChange={setTab}
            footer={
                <EditorFooter
                    onCancel={onClose}
                    onSave={tab === 'text' ? saveLocale : saveCard}
                    saving={saving}
                    left={
                        !isCreate && tab === 'card' ? (
                            <Button color='red' variant='light' onClick={remove}>
                                {t('pages.districts.editor.delete')}
                            </Button>
                        ) : undefined
                    }
                />
            }
        >
            {tab === 'card' && (
                <Stack gap='lg'>
                    <Section title={t('pages.districts.editor.identity')}>
                        <TextInput
                            label={t('pages.districts.editor.title')}
                            required
                            {...form.getInputProps('title')}
                        />
                        <TextInput
                            label={t('pages.districts.editor.slug')}
                            description={t('pages.districts.editor.slugHint')}
                            {...form.getInputProps('slug')}
                        />
                        <CitySearchSelect
                            label={t('pages.districts.editor.city')}
                            value={form.values.cityId}
                            onChange={(cityId) => form.setFieldValue('cityId', cityId)}
                            initialLabel={row?.cityTitle}
                        />
                        <Group grow>
                            <TextInput label={t('pages.districts.editor.lat')} {...form.getInputProps('lat')} />
                            <TextInput label={t('pages.districts.editor.lng')} {...form.getInputProps('lng')} />
                        </Group>
                        <Select
                            label={t('pages.districts.editor.status')}
                            description={t('pages.districts.editor.statusHint')}
                            data={statusOptions}
                            {...form.getInputProps('status')}
                        />
                        <TextInput
                            label={t('pages.districts.editor.coverUrl')}
                            description={t('pages.districts.editor.coverUrlHint')}
                            {...form.getInputProps('coverUrl')}
                        />
                        <Group grow>
                            <TextInput
                                label={t('pages.districts.editor.coverAuthor')}
                                {...form.getInputProps('coverAuthor')}
                            />
                            <TextInput
                                label={t('pages.districts.editor.coverLicense')}
                                {...form.getInputProps('coverLicense')}
                            />
                        </Group>
                        <TextInput
                            label={t('pages.districts.editor.coverSourceUrl')}
                            {...form.getInputProps('coverSourceUrl')}
                        />
                    </Section>
                </Stack>
            )}
            {tab === 'text' && !isCreate && (
                <Stack gap='lg'>
                    <LanguageChips
                        languages={chips}
                        value={lang}
                        onChange={(value) => setLang(value as Languages)}
                    />
                    <Section
                        title={t('pages.districts.editor.copyTitle')}
                        description={t('pages.districts.editor.copyHint')}
                    >
                        <TextInput
                            label={t('pages.districts.editor.name')}
                            value={current.name}
                            onChange={(event) => patchDraft({ name: event.currentTarget.value })}
                        />
                        <TextInput
                            label={t('pages.districts.editor.localName')}
                            description={t('pages.districts.editor.localNameHint')}
                            value={current.localName}
                            onChange={(event) => patchDraft({ localName: event.currentTarget.value })}
                        />
                        <TextInput
                            label={t('pages.districts.editor.characterLine')}
                            description={t('pages.districts.editor.characterLineHint')}
                            value={current.characterLine}
                            onChange={(event) => patchDraft({ characterLine: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.districts.editor.portrait')}
                            description={t('pages.districts.editor.portraitHint')}
                            minRows={3}
                            autosize
                            value={current.portrait}
                            onChange={(event) => patchDraft({ portrait: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.districts.editor.why')}
                            description={t('pages.districts.editor.whyHint')}
                            minRows={3}
                            autosize
                            value={current.why}
                            onChange={(event) => patchDraft({ why: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.districts.editor.stay')}
                            description={t('pages.districts.editor.stayHint')}
                            minRows={3}
                            autosize
                            value={current.stay}
                            onChange={(event) => patchDraft({ stay: event.currentTarget.value })}
                        />
                        <TextInput
                            label={t('pages.districts.editor.streets')}
                            description={t('pages.districts.editor.streetsHint')}
                            value={current.streets}
                            onChange={(event) => patchDraft({ streets: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.districts.editor.knownFor')}
                            description={t('pages.districts.editor.knownForHint')}
                            minRows={2}
                            autosize
                            value={current.knownFor}
                            onChange={(event) => patchDraft({ knownFor: event.currentTarget.value })}
                        />
                        <TextInput
                            label={t('pages.districts.editor.tags')}
                            description={t('pages.districts.editor.tagsHint')}
                            value={current.tags}
                            onChange={(event) => patchDraft({ tags: event.currentTarget.value })}
                        />
                        <TextInput
                            label={t('pages.districts.editor.guideName')}
                            value={current.guideName}
                            onChange={(event) => patchDraft({ guideName: event.currentTarget.value })}
                        />
                        <TextInput
                            label={t('pages.districts.editor.guideRole')}
                            value={current.guideRole}
                            onChange={(event) => patchDraft({ guideRole: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.districts.editor.guideTip')}
                            description={t('pages.districts.editor.guideTipHint')}
                            minRows={2}
                            autosize
                            value={current.guideTip}
                            onChange={(event) => patchDraft({ guideTip: event.currentTarget.value })}
                        />
                        <Switch
                            label={t('pages.districts.editor.ready')}
                            description={t('pages.districts.editor.readyHint')}
                            checked={current.status === GuideLocaleStatusEnum.READY}
                            onChange={(event) =>
                                patchDraft({
                                    status: event.currentTarget.checked
                                        ? GuideLocaleStatusEnum.READY
                                        : GuideLocaleStatusEnum.EMPTY,
                                })
                            }
                        />
                    </Section>
                </Stack>
            )}
        </EntityEditorShell>
    );
};
